import { ChangeDetectionStrategy, Component, effect, forwardRef, inject, input, output, signal } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  IconComponent,
  LoaderComponent,
  TRANSLATE_SERVICE_TOKEN,
  TranslateInterface
} from '@tribeauto/ngx-mobilite-material';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDragHandle,
  CdkDragPreview,
  CdkDropList,
  moveItemInArray
} from '@angular/cdk/drag-drop';
import { baseItems, ColumnPreferenceData, prefixLabelRoute } from './records-preferences.model';
import { MatCheckbox } from '@angular/material/checkbox';
import { PreferencesService } from '../../preferences/preferences.service';
import { ApplicationStore } from '../../../../core/services/application-store.service';
import { catchError, map, Observable, of } from 'rxjs';
import { UserTablePreferences } from '../../preferences/preferences.model';
import { AsyncPipe } from '@angular/common';

@Component({
  selector: 'pvmo-records-preferences',
  imports: [
    TranslatePipe,
    ReactiveFormsModule,
    CdkDropList,
    CdkDrag,
    IconComponent,
    CdkDragHandle,
    MatCheckbox,
    CdkDragPreview,
    AsyncPipe,
    LoaderComponent
  ],
  providers: [
    {
      provide: TRANSLATE_SERVICE_TOKEN,
      useFactory: (translateService: TranslateService): TranslateInterface => ({
        get: (key: string): string => translateService.instant(key)
      }),
      deps: [TranslateService]
    },
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RecordsPreferencesComponent),
      multi: true
    }
  ],
  templateUrl: './records-preferences.component.html',
  styleUrl: './records-preferences.component.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RecordsPreferencesComponent implements ControlValueAccessor {
  private readonly preferencesService = inject(PreferencesService);
  private readonly applicationStore = inject(ApplicationStore);
  openModifColumns = input<boolean>();
  isError = output<boolean>();
  isLoadingData$: Observable<boolean> | undefined = undefined;
  columnPreferenceDataList: ColumnPreferenceData[] = [];
  hasError = signal(false);

  constructor() {
    effect(() => {
      const openModifColumns = this.openModifColumns();
      if (openModifColumns) {
        this.hasError.set(false);
        this.preferencesService.updateRecordsPreferencesValid(false);
        this.isLoadingData$ = this.preferencesService.getUserRecordsPreferences().pipe(
          map(userTablePreferencesResponse => {
            if (userTablePreferencesResponse?.columnsOrder && userTablePreferencesResponse?.columnsDisplayed) {
              this.columnPreferenceDataList = this.getColumnPreferencesDataList(userTablePreferencesResponse);
            } else {
              this.columnPreferenceDataList = [...baseItems];
            }
            return true;
          }),
          catchError(() => {
            this.hasError = signal(true);
            this.isError.emit(true);
            this.columnPreferenceDataList = [];
            return of(false);
          })
        );
      } else {
        this.applicationStore.update('recordsPreferences', this.retrieveUserTablePreferences());
        this.preferencesService.updateRecordsPreferencesValid(true);
      }
    });
  }

  writeValue(value: ColumnPreferenceData[]): void {
    this.columnPreferenceDataList = Array.isArray(value) ? [...value] : [];
  }

  registerOnChange(fn: (value: ColumnPreferenceData[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  onDrop(event: CdkDragDrop<ColumnPreferenceData[]>): void {
    moveItemInArray(this.columnPreferenceDataList, event.previousIndex, event.currentIndex);
    this.emitChange();
  }

  onToggle(item: ColumnPreferenceData): void {
    item.checked = !item.checked;
    this.emitChange();
  }

  private emitChange(): void {
    this.onTouched();
    this.onChange(Array.isArray(this.columnPreferenceDataList) ? [...this.columnPreferenceDataList] : []);
  }

  private onChange: (value: ColumnPreferenceData[]) => void = () => {};

  private onTouched: () => void = () => {};

  private getColumnPreferencesDataList(userTablePreferencesResponse: UserTablePreferences): ColumnPreferenceData[] {
    const list: ColumnPreferenceData[] = [];
    const entries: [string, number][] = Object.entries(userTablePreferencesResponse.columnsOrder);
    const columsOrderSorted = [...entries].sort(([, a], [, b]) => a - b);
    for (const [column] of columsOrderSorted) {
      list.push({
        key: column,
        label: prefixLabelRoute.concat(column),
        checked: userTablePreferencesResponse.columnsDisplayed.includes(column)
      });
    }
    if (list.length !== baseItems.length) {
      const itemsToAdd = baseItems.filter(item => !list.some(column => column.key === item.key));
      for (const item of itemsToAdd) {
        list.splice(baseItems.indexOf(item) - 1, 0, item);
      }
    }
    return list ?? [...baseItems];
  }

  private retrieveUserTablePreferences(): UserTablePreferences {
    const columnsOrder: Record<string, number> = {};
    const columnsDisplayed: string[] = [];
    for (const columnPreferenceData of this.columnPreferenceDataList) {
      if (columnPreferenceData.checked) {
        columnsDisplayed.push(columnPreferenceData.key);
      }
      columnsOrder[columnPreferenceData.key] = this.columnPreferenceDataList.indexOf(columnPreferenceData) + 1;
    }
    return {
      columnsOrder,
      columnsDisplayed
    };
  }
}
